import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@mavatech/api/router";

export const trpc = createTRPCReact<AppRouter>();

export function createTrpcClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: "/trpc",
        transformer: superjson,
        // Viele gleichzeitige Queries auf einer Seite können sich sonst zu einer
        // GET-URL summieren, die den Server-Limit überschreitet (414) — ab dieser
        // Länge wird automatisch auf mehrere Batch-Anfragen aufgeteilt.
        maxURLLength: 2000,
        fetch(url, options) {
          // Abfragen (GET) bekommen ein Zeitlimit: hängt eine Verbindung (WLAN-Wechsel, Ruhezustand des
          // Rechners, VPN), wird sie abgebrochen und automatisch neu versucht, statt ewig "Lädt …" zu zeigen.
          // Änderungen (POST: Speichern, PDF, Import) bleiben ohne Limit — die dürfen länger dauern.
          const isQuery = (options?.method ?? "GET").toUpperCase() === "GET";
          const signal =
            isQuery &&
            typeof AbortSignal !== "undefined" &&
            "timeout" in AbortSignal
              ? options?.signal
                ? anySignal([
                    options.signal as AbortSignal,
                    AbortSignal.timeout(25_000),
                  ])
                : AbortSignal.timeout(25_000)
              : options?.signal;
          return fetch(url, {
            ...options,
            signal: signal ?? undefined,
            credentials: "include",
          });
        },
      }),
    ],
  });
}

function anySignal(signals: AbortSignal[]): AbortSignal {
  if ("any" in AbortSignal)
    return (
      AbortSignal as unknown as { any(s: AbortSignal[]): AbortSignal }
    ).any(signals);
  const c = new AbortController();
  for (const sg of signals) {
    if (sg.aborted) c.abort();
    else sg.addEventListener("abort", () => c.abort(), { once: true });
  }
  return c.signal;
}

/** Fehler, bei denen ein erneuter Versuch nichts bringt (Berechtigung, nicht gefunden, ungültige Eingabe). */
export function isClientError(err: unknown): boolean {
  const status = (err as { data?: { httpStatus?: number } } | null)?.data
    ?.httpStatus;
  return typeof status === "number" && status >= 400 && status < 500;
}
